import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { usePlayer } from "@/contexts/PlayerContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RequestForm = ({ source = "work-page", testid = "request" }) => {
  const [form, setForm] = useState({ message: "", name: "", whatsapp: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || !form.name.trim() || !form.whatsapp.trim()) {
      setError("Fill all three fields — it takes twenty seconds.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/requests`, { ...form, source });
      setSent(true);
    } catch {
      setError("Something went wrong. Try again, or reach us on WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid={`${testid}-success`} className="py-6 text-center">
        <span className="mx-auto w-14 h-14 rounded-full bg-[#C8F031] flex items-center justify-center">
          <Check className="w-6 h-6 text-black" strokeWidth={3} />
        </span>
        <p className="mt-6 font-heading text-xl font-bold text-white">Got it.</p>
        <p className="mt-2 text-sm text-white/50 leading-relaxed max-w-xs mx-auto">
          We'll send the closest example we've made within 24 hours, on WhatsApp.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-black border border-white/15 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-[#C8F031] transition-colors min-h-[44px]";

  return (
    <form onSubmit={submit} data-testid={`${testid}-form`} className="space-y-3">
      <textarea
        data-testid={`${testid}-message`}
        value={form.message}
        onChange={set("message")}
        rows={3}
        placeholder='e.g. "45-second skincare ad, fast cuts, trending audio"'
        className={`${inputCls} resize-none`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid={`${testid}-name`}
          value={form.name}
          onChange={set("name")}
          placeholder="Name"
          className={inputCls}
        />
        <input
          data-testid={`${testid}-whatsapp`}
          value={form.whatsapp}
          onChange={set("whatsapp")}
          placeholder="WhatsApp number"
          inputMode="tel"
          className={inputCls}
        />
      </div>
      {error && (
        <p data-testid={`${testid}-error`} className="text-xs text-red-400">
          {error}
        </p>
      )}
      <button
        data-testid={`${testid}-submit`}
        type="submit"
        disabled={busy}
        className="btn-primary w-full sm:w-auto disabled:opacity-50"
      >
        {busy ? "Sending…" : "Send it"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
};

export const QuoteDialog = () => {
  const { quoteOpen, closeQuote } = usePlayer();
  return (
    <Dialog open={quoteOpen} onOpenChange={(o) => !o && closeQuote()}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-[#111] border-white/10 text-white rounded-3xl max-w-md"
      >
        <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight text-white pr-8">
          Tell us what you're making.
        </DialogTitle>
        <p className="text-sm text-white/50 -mt-2">
          One line about the video. We reply on WhatsApp within 24 hours with the closest example we've cut — and you can claim your free 30-second edit right there.
        </p>
        <RequestForm source="quote-dialog" testid="quote" />
      </DialogContent>
    </Dialog>
  );
};

export default RequestForm;
